import React from 'react';
import { motion } from 'framer-motion';

interface Fr00tLogoProps {
  size?: number;
  showText?: boolean;
  textSize?: 'small' | 'medium' | 'large';
  className?: string;
  style?: React.CSSProperties;
  animated?: boolean;
}

/**
 * Fr00t Logo Component
 * 
 * Numerically and fractally structured grapes logo based on:
 * - Fibonacci sequence (8 grapes)
 * - Golden ratio proportions (φ = 1.618...)
 * - Fr00t numerology: F(6) + R(9) + 0(0) + 0(0) + T(2) = 17 → 8
 */
const Fr00tLogo: React.FC<Fr00tLogoProps> = ({
  size = 32,
  showText = false,
  textSize = 'medium',
  className = '',
  style = {},
  animated = false
}) => {
  const textSizeMap = {
    small: '0.875rem',
    medium: '1rem',
    large: '1.5rem'
  };

  const logoContent = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      style={style}
    >
      <defs>
        <linearGradient id="grapeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#7c3aed" stopOpacity="1" />
          <stop offset="50%" stopColor="#6366f1" stopOpacity="1" />
          <stop offset="100%" stopColor="#8b5cf6" stopOpacity="1" />
        </linearGradient>
        <linearGradient id="stemGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#10b981" stopOpacity="1" />
          <stop offset="100%" stopColor="#059669" stopOpacity="1" />
        </linearGradient>
        <radialGradient id="grapeHighlight" cx="30%" cy="30%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>
      
      {/* Stem */}
      <path d="M 50 8 Q 48 18 50 28" 
            stroke="url(#stemGradient)" 
            strokeWidth="2.5" 
            fill="none" 
            strokeLinecap="round"/>
      
      {/* Fibonacci spiral arrangement of 8 grapes */}
      <circle cx="50" cy="28" r="9" fill="url(#grapeGradient)" opacity="0.95"/>
      <circle cx="52" cy="26" r="3" fill="url(#grapeHighlight)"/>
      
      <circle cx="40" cy="32" r="9" fill="url(#grapeGradient)" opacity="0.95"/>
      <circle cx="42" cy="30" r="3" fill="url(#grapeHighlight)"/>
      
      <circle cx="60" cy="32" r="8" fill="url(#grapeGradient)" opacity="0.95"/>
      <circle cx="62" cy="30" r="2.5" fill="url(#grapeHighlight)"/>
      
      <circle cx="36" cy="42" r="7.5" fill="url(#grapeGradient)" opacity="0.95"/>
      <circle cx="38" cy="40" r="2.5" fill="url(#grapeHighlight)"/>
      
      <circle cx="45" cy="48" r="7" fill="url(#grapeGradient)" opacity="0.95"/>
      <circle cx="47" cy="46" r="2.5" fill="url(#grapeHighlight)"/>
      
      <circle cx="58" cy="46" r="6.5" fill="url(#grapeGradient)" opacity="0.95"/>
      <circle cx="60" cy="44" r="2" fill="url(#grapeHighlight)"/>
      
      <circle cx="40" cy="58" r="6" fill="url(#grapeGradient)" opacity="0.95"/>
      <circle cx="42" cy="56" r="2" fill="url(#grapeHighlight)"/>
      
      <circle cx="55" cy="60" r="5.5" fill="url(#grapeGradient)" opacity="0.95"/>
      <circle cx="57" cy="58" r="2" fill="url(#grapeHighlight)"/>
      
      {/* Fractal detail: Smaller grapes (representing "00" - infinite potential) */}
      <circle cx="32" cy="38" r="3.5" fill="url(#grapeGradient)" opacity="0.7"/>
      <circle cx="65" cy="40" r="3.5" fill="url(#grapeGradient)" opacity="0.7"/>
      <circle cx="50" cy="52" r="3.5" fill="url(#grapeGradient)" opacity="0.7"/>
      
      {/* Golden ratio connecting lines */}
      <path d="M 50 28 L 40 32 L 36 42 L 40 58" 
            stroke="rgba(124,58,237,0.1)" 
            strokeWidth="0.5" 
            fill="none" 
            strokeDasharray="1,2"/>
      <path d="M 50 28 L 60 32 L 58 46 L 55 60" 
            stroke="rgba(124,58,237,0.1)" 
            strokeWidth="0.5" 
            fill="none" 
            strokeDasharray="1,2"/>
    </svg>
  );

  if (animated) {
    return (
      <motion.div
        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        {logoContent}
        {showText && (
          <span style={{ fontSize: textSizeMap[textSize], fontWeight: 600 }}>
            Fr00t
          </span>
        )}
      </motion.div>
    );
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
      {logoContent}
      {showText && (
        <span style={{ fontSize: textSizeMap[textSize], fontWeight: 600 }}>
          Fr00t
        </span>
      )}
    </div>
  );
};

export default Fr00tLogo;

